import { useLocation } from "react-router-dom";
import { useEffect, useState, useContext, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { AlertTriangle, X } from "lucide-react";
import PageHeading from "../components/ui/page-heading";
import "../essential.css";
import SideMenu from "../components/Menus/SideMenu";
import UpperMenu from "../components/Menus/UpperMenu";
import Models from "../components/Models";
import Pricing from "../components/Pricing";
import RateLimits from "../components/RateLimits";
import BillingDashboard from "../components/Dashboard";
import APIKeys from "../components/APIKeys";
import Recharge from "../components/Recharge";
import Receipts from "../components/Receipts";
import Info from "../components/Info";
import BatchAPI from "../components/BatchApi";
import Files from "../components/Files";
import Organizations from "../components/Organizations";
import PlaygroundPage from "../components/Playground/PlaygroundPage";
import { TitleContext } from "../contexts/TitleContext";
import { ThemeContext } from "../contexts/ThemeContext";
import { API_BASE } from "../shared/Constants";

function Home() {
  const { pathname } = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => { contentRef.current?.scrollTo({ top: 0 }); }, [pathname]);
  const { isAuthenticated, isLoading } = useAuth0();
  const { title } = useContext(TitleContext);
  const { isDarkMode } = useContext(ThemeContext);
  const [maintenanceBanner, setMaintenanceBanner] = useState(false);
  const [maintenanceEta, setMaintenanceEta] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/maintenance-status`)
      .then((res) => res.json())
      .then((data) => {
        setMaintenanceBanner(data.maintenance_mode);
        setMaintenanceEta(data.maintenance_eta);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasCode = params.has("code");
    const hasError = params.has("error");
    if (!isLoading && !isAuthenticated) {
      if (hasError) {
        const search = params.toString();
        window.location.href = `/auth${search ? `?${search}` : ""}`;
        return;
      }
      if (!hasCode) window.location.href = "/auth";
    }
  }, [isLoading, isAuthenticated]);

  // Reflect ThemeContext as a `dark` class on <html> so Tailwind dark variants work
  useEffect(() => {
    if (isDarkMode) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  }, [isDarkMode]);

  if (isLoading || !isAuthenticated) return null;

  return (
    <div data-testid="platform-shell" className="essential-shell flex h-screen w-full overflow-hidden bg-background text-foreground">
      <SideMenu />
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <UpperMenu title={title} />
        {maintenanceBanner && !bannerDismissed && (
          <div className="flex items-center gap-3 border-b border-warning/30 bg-warning/10 px-6 py-3 md:px-8">
            <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
            <p className="flex-1 text-sm text-foreground/90">
              <strong className="text-foreground">Estamos em manutenção.</strong> Pagamentos e
              algumas operações estão temporariamente desabilitados.{" "}
              <strong className="text-foreground">Todo o uso via API está gratuito</strong> até o
              término da manutenção.
              {maintenanceEta && (
                <>
                  {" "}
                  Previsão de término: <strong className="text-foreground">{maintenanceEta}</strong>.
                </>
              )}
            </p>
            <button
              onClick={() => setBannerDismissed(true)}
              aria-label="Fechar aviso"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-foreground/60 transition-colors hover:bg-warning/20 hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <div ref={contentRef} className="min-w-0 flex-1 overflow-y-auto bg-transparent">
          <div data-testid="platform-page-frame" className="platform-page-frame w-full max-w-[1600px] px-4 py-6 md:px-6 md:py-7">
            {!["Plataforma", "Gastos", "Chaves de API", "Recarga"].includes(title) && <PageHeading title={title} />}
            {title === "Plataforma" && <Info />}
            {title === "Modelos" && <Models />}
            {title === "Preços" && <Pricing />}
            {title === "Rate Limits" && <RateLimits />}
            {title === "Gastos" && <BillingDashboard />}
            {title === "Chaves de API" && <APIKeys />}
            {title === "Recarga" && <Recharge />}
            {title === "Comprovantes" && <Receipts />}
            {title === "Batch API" && <BatchAPI />}
            {title === "Arquivos" && <Files />}
            {title === "Organizações" && <Organizations />}
            {title === "Playground" && <PlaygroundPage />}
          </div>
        </div>
      </main>
    </div>
  );
}

export default Home;
